import { makeStyles } from '@material-ui/core';
import { FC } from 'react';
import {
  AndroidApp,
  AppleApp
} from 'src/services/napi/projects/interfaces/fetchProjects.interface';
import { AppType } from 'src/page/DynamicLinksConfig/interfaces/appType.interface';
import CButton from 'src/component/Button';

const useStyle = makeStyles({
  content: {
    padding: '24px 0',
    textAlign: 'left'
  },
  sectionText: {
    fontSize: '14px',
    lineHeight: '17px',
    margin: '8px 24px 0px 24px',
    wordBreak: 'break-all'
  },
  sectionTitle: {
    color: 'rgba(0, 0, 0, 0.55)',
    fontSize: '13px',
    lineHeight: '15px',
    margin: '24px 24px 0px 24px'
  }
});

interface ProjectAppsAppDetailsProps {
  appData: AndroidApp | AppleApp;
  appType: AppType;
  onClickEdit: () => void;
}

const ProjectAppsAppDetails: FC<ProjectAppsAppDetailsProps> = ({
  appData,
  appType,
  onClickEdit
}) => {
  const classes = useStyle();

  const downloadAssetlinksJson = () => {
    const { packageName, sha256CertFingerprints } = appData as AndroidApp;

    const content = [
      {
        relation: ['delegate_permission/common.handle_all_urls'],
        target: {
          namespace: 'android_app',
          package_name: packageName,
          sha256_cert_fingerprints: sha256CertFingerprints
        }
      }
    ];

    const contentStr = JSON.stringify(content);
    const file = new Blob([contentStr], { type: 'application/json;charset=utf-8' });

    const link = document.createElement('a');
    link.href = URL.createObjectURL(file);
    link.download = 'assetlinks.json';
    link.click();
    URL.revokeObjectURL(link.href);
  };

  const downloadAppleAppSiteAssociation = () => {
    const { teamId, bundleId } = appData as AppleApp;

    const content = {
      applinks: {
        apps: [],
        details: [
          {
            appID: `${teamId}.${bundleId}`,
            paths: ['NOT /_/*', '/*']
          }
        ]
      }
    };

    const contentStr = JSON.stringify(content);
    const file = new Blob([contentStr], { type: 'text/text' });

    const link = document.createElement('a');
    link.href = URL.createObjectURL(file);
    link.download = 'apple-app-site-association';
    link.click();
    URL.revokeObjectURL(link.href);
  };

  return (
    <div className={classes.content}>
      <CButton onClick={onClickEdit} type="secondary" xstyle={{ marginLeft: '24px' }}>
        Edit
      </CButton>

      {appType === AppType.android && (
        <CButton onClick={downloadAssetlinksJson} type="primary">
          Download assetlinks.json
        </CButton>
      )}

      {appType === AppType.apple && (
        <CButton onClick={downloadAppleAppSiteAssociation} type="primary">
          Download apple-app-site-association
        </CButton>
      )}

      <div className={classes.sectionTitle}>App name</div>
      <div className={classes.sectionText}>{appData.name}</div>

      <div className={classes.sectionTitle}>App icon</div>
      <div className={classes.sectionText}>{appData.icon}</div>

      {appType === AppType.android && (
        <>
          <div className={classes.sectionTitle}>Package name</div>
          <div className={classes.sectionText}>{(appData as AndroidApp).packageName}</div>

          <div className={classes.sectionTitle}>SHA-256 certificate fingerprints</div>
          {(appData as AndroidApp).sha256CertFingerprints?.map((cert) => (
            <div key={cert} className={classes.sectionText}>
              {cert}
            </div>
          ))}
        </>
      )}

      {appType === AppType.apple && (
        <>
          <div className={classes.sectionTitle}>Bundle ID</div>
          <div className={classes.sectionText}>{(appData as AppleApp).bundleId}</div>

          <div className={classes.sectionTitle}>App Store ID</div>
          <div className={classes.sectionText}>{(appData as AppleApp).appStoreId}</div>

          <div className={classes.sectionTitle}>Team ID</div>
          <div className={classes.sectionText}>{(appData as AppleApp).teamId}</div>
        </>
      )}
    </div>
  );
};

export default ProjectAppsAppDetails;
